import { GeneratedAttachmentResponse } from "@/Common/Api/Attachments/Dto/Responses/GeneratedAttachmentResponse";
import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ExternalEmployeeResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/ExternalEmployeeResponse";
import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { AttachDocumentLink } from "@/Common/Components/IconControls/AttachDocumentLink";
import { AttachedDocumentList } from "@/Common/Components/IconControls/AttachedDocumentList";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { FileSelectionContainerType } from "@/Common/Models/FileSelection/FileSelectionContainerType";
import { FileType } from "@/Common/Models/FileType";
import { IPatient } from "@/Common/Models/Patient/IPatient";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MessageResponse } from "@/Communications/Api/Dto/Responses/MessageResponse";
import { SearchExternalEmployeeControl } from "@/Communications/Components/Search/SearchExternalEmployeesControl";
import { FileSelectionContainer } from "@/ExportableFile/Components/FileSelection/FileSelectionContainer";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFileModel } from "@/ExportableFile/Models/ExportableFileTableItem";

interface IBaseExternalMessageFormProps {
    componentId?: string;
    onClose?: () => void;
    onSend?: () => void;
}

class BaseExternalMessageFormState {
    public messageModel: MessageResponse;
    public selectedPatient: IPatient;
    public recipients: ExternalEmployeeResponse[];
    public generatedAttachment: GeneratedAttachmentResponse;
    public shouldIncludeSearchFromSameClient: boolean;

    public isSubjectClickableTextShown: boolean;
    public isBodyClickableTextShown: boolean;
    public shouldClearCacheState: boolean;

    public showFileSelection: boolean;
    public selectedAttachments: ExportableFileModel[];
    public exportableFileToPreview: ExportableFileModel;
    public unattachableFiles: string[];
    public refusalReasonLabel: string;
    public attachmentIdToPreview: string;

    public isSending: boolean;

    public static createDefault(): BaseExternalMessageFormState {
        let state = new BaseExternalMessageFormState();
        state.messageModel = new MessageResponse();
        state.recipients = [];
        state.selectedPatient = null;
        state.generatedAttachment = null;
        state.isSubjectClickableTextShown = false;
        state.isBodyClickableTextShown = false;
        state.selectedAttachments = [];
        state.isSending = false;
        state.unattachableFiles = [];
        state.refusalReasonLabel = "";
        state.shouldIncludeSearchFromSameClient = false;
        state.isSubjectClickableTextShown = false;
        state.isBodyClickableTextShown = false;
        return state;
    }
}

abstract class BaseExternalMessageFormComponent<P extends IBaseExternalMessageFormProps, S extends BaseExternalMessageFormState> extends BaseReactComponent<P, S> {
    public render(): JSX.Element {
        if (!this.state || !this.state.messageModel)
            return this.renderEmpty();

        return (
            <div
                id={this.props.componentId || "external-msg-form"}
                onClick={() => this.containerClick()}
            >
                {this.renderAttachmentPreview(this.state.exportableFileToPreview, this.state.attachmentIdToPreview)}
                {this.renderUnattachableFilesWarning()}
                {this.renderFileSelection()}
                {this.renderHeader()}
                {this.renderBody()}
                {this.renderFooter()}
            </div>
        );
    }

    protected renderEmpty(): JSX.Element {
        return null;
    }

    protected renderHeader(): JSX.Element {
        return (
            <div>
                <p className="header-font-color">{cacheStore.GetString("ExternalCommunicationsLabel")}</p>
                <h3 className="thin-font">{cacheStore.GetString("NewMessageLabel")}</h3>
            </div>
        );
    }

    protected renderBody(): JSX.Element {
        return (
            <div>
                {this.renderRow(
                    "ToM2Label",
                    <SearchExternalEmployeeControl
                        valid={!isArrayNullOrUndefinedOrEmpty(this.state.recipients)}
                        componentId="search_external_employee"
                        selectedEmployees={this.state.recipients}
                        includeSearchFromSameClient={this.state.shouldIncludeSearchFromSameClient}
                        maxSelection={1}
                        onSelected={emp => this.mutateState(s => s.recipients.push(emp))}
                        onClear={emp => this.mutateState(s => emp && s.recipients.splice(s.recipients.indexOf(emp), 1))}
                    />
                )}
                {this.renderRow("PatientLabel", this.renderPatient(this.state.selectedPatient))}
                {this.renderRow(
                    "SubjectLabel",
                    <div>
                        <TextboxControl
                            debounceMs={200}
                            onChange={(value: string) => {
                                this.mutateState(s => s.messageModel.Subject = value);
                            }}
                            className="form-control"
                            value={this.state.messageModel.Subject || ""}
                            valid={this.areSubjectAndBodyValid}
                            id="externalcomm_create_subject"
                        />
                        {this.state.isSubjectClickableTextShown && (
                            <div className="clickable-text-container displace-correction">
                                <ClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="SMESSAGES"
                                    inputElementId="externalcomm_create_subject"
                                    onCloseEvent={() => this.mutateState(m => m.isSubjectClickableTextShown = false)}
                                />
                            </div>
                        )}
                        <div className="text-right">
                            <a onClick={e => this.toggleSubjectClickableTextVisibility(e)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                        </div>
                    </div>
                )}
                {this.renderRow(
                    "BodyLabel",
                    <div>
                        <TextAreaAutoSize
                            id={"externalcomm_create_body_" + this.state.messageModel.Id}
                            value={this.state.messageModel.Body || ""}
                            onTextChangeCallback={(value: string) => this.mutateState(s => s.messageModel.Body = value)}
                            maxHeight="205px"
                            minHeight="100px"
                            isInvalid={!this.areSubjectAndBodyValid}
                        />
                        {this.state.isBodyClickableTextShown && (
                            <div className="clickable-text-container displace-correction">
                                <ClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="BMESSAGES"
                                    inputElementId={"externalcomm_create_body_" + this.state.messageModel.Id}
                                    onCloseEvent={() => this.mutateState(m => m.isBodyClickableTextShown = false)}
                                />
                            </div>
                        )}
                        <div className="text-right">
                            <a onClick={e => this.toggleBodyClickableTextVisibility(e)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                        </div>
                    </div>
                )}
                {this.state.selectedPatient && this.renderRow("AttachmentsLabel", this.renderAttachmentRow())}
            </div>
        );
    }

    protected renderFooter(): JSX.Element {
        return (
            <div className="col-xs-12 text-right">
                <ButtonControl
                    className="btn btn-default push-right-5"
                    label={cacheStore.GetString("CancelLabel")}
                    onClick={() => this.mutateState(s => s.shouldClearCacheState = true, () => this.props.onClose && this.props.onClose())}
                />
                <ButtonControl
                    className="btn btn-primary"
                    label={cacheStore.GetString("SendLabel")}
                    disabled={!this.isFormValid || this.state.isSending}
                    onClick={() => this.mutateState(s => s.isSending = true, () => this.handleSend())}
                />
            </div>
        );
    }

    protected renderAttachmentPreview(fileModel?: ExportableFileModel, blobAttachmentId?: string): JSX.Element {
        if (!fileModel && !blobAttachmentId)
            return null;

        this.mutateState(
            s => {
                s.exportableFileToPreview = null;
                s.attachmentIdToPreview = null;
            },
            () => {
                if (fileModel)
                    fileModel.Model.openInNewWindow();

                if (blobAttachmentId)
                    MyleHelper.OpenNewWindow(PrintHelper.GetAttachmentPreviewPath(blobAttachmentId));
            }
        );
    }

    protected renderRow(label: string, field: JSX.Element): JSX.Element {
        return (
            <div className="row" style={{ minHeight: "60px" }}>
                <div className="col-md-2 col-sm-12 vcenter text-right">
                    <label className="form-label">{cacheStore.GetString(label)}</label>
                </div>
                <div className="col-md-9 col-sm-12">
                    {field}
                </div>
            </div>
        );
    }

    protected renderPatient(selectedPatient: IPatient): JSX.Element {
        if (selectedPatient && !selectedPatient.isInternal()) {
            return (
                <div style={{ paddingTop: "5px", minHeight: "38px" }}>
                    <p>
                        {NameHelper.formatFullNameForHasName(selectedPatient)}
                        <strong style={{ color: "red", cursor: "pointer" }} onClick={() => this.mutateState(s => { s.selectedPatient = null; s.generatedAttachment = null; })}> X </strong>
                    </p>
                </div>
            );
        }

        return (
            <PersonSearchAutocompleteCustom
                useBlurEvent={true}
                componentId="externalcomm_create_addpatient"
                selectedItemList={selectedPatient == null ? [].toImmutable() : [new Person(selectedPatient)].toImmutable()}
                onSelectionCallback={personItem => this.mutateState(s => {
                    s.selectedPatient = InternalPatientResponse.fromPerson(personItem.Item);
                    s.generatedAttachment = null;
                })}
                onClearCallback={() => this.mutateState(s => {
                    s.selectedPatient = null;
                    s.selectedAttachments = [];
                })}
                focusOnLoad={false}
                closeDropDownAfterSelection={true}
            />
        );
    }

    protected renderAttachmentRow(): JSX.Element {
        if (!this.state.selectedPatient)
            return null;

        return (
            <div>
                {this.state.selectedPatient.isInternal() &&
                    this.renderInternalAttachment(this.state.selectedAttachments)
                }
                {this.state.generatedAttachment?.AttachmentId && isArrayNullOrUndefinedOrEmpty(this.state.selectedAttachments) &&
                    this.renderAttachmentStoragePath(this.state.selectedPatient, this.state.generatedAttachment.AttachmentId)
                }
            </div>
        );
    }

    protected renderInternalAttachment(selectedAttachments: ExportableFileModel[]): JSX.Element {
        return (
            <div>
                <AttachDocumentLink onClick={() => this.mutateState(s => s.showFileSelection = true)} />
                <div>
                    {selectedAttachments.length > 0 && (
                        <AttachedDocumentList
                            selectedAttachments={selectedAttachments}
                            removeAttachmentFromList={itemId => this.removeAttachment(itemId)}
                            resolveDescription={p => p.displayDescription}
                        />
                    )}
                </div>
            </div>
        );
    }

    protected renderAttachmentStoragePath(patient: IPatient, blobAttachmentId: string): JSX.Element {
        return (
            <div style={{ marginTop: "6px" }}>
                <a onClick={() => this.mutateState(s => s.attachmentIdToPreview = blobAttachmentId)}>
                    {NameHelper.formatFullNameForHasName(patient, NameHelperFormat.FirstNameFirst)}_{cacheStore.GetString("AttachmentLabel")}.pdf
                </a>
                <strong onClick={() => this.mutateState(s => s.generatedAttachment = null)} style={{ cursor: "pointer", color: "red" }}> X </strong>
            </div>
        );
    }

    protected renderFileSelection(): JSX.Element | null {
        if (!this.state.showFileSelection || !this.state.selectedPatient)
            return null;

        let patient = new Person(this.state.selectedPatient);
        return (
            <FileSelectionContainer
                title={cacheStore.GetString("AttachFilesLabel")}
                patient={patient}
                type={FileSelectionContainerType.Attachment}
                onClose={() => this.mutateState(s => s.showFileSelection = false)}
                onPrintOrAttachClicked={files => this.handleOnFileAttached(files)}
                autoManageOverlay={true}
            />
        );
    }

    protected handleOnFileAttached(files: ExportableFile[]): void {
        let documentFiles = files.filter(x => x.fileType === FileType.DOCUMENT || x.fileType === FileType.LABPDF);
        if (!isArrayNullOrUndefinedOrEmpty(documentFiles)) {
            this.canDocumentFilesConvertToPDF(
                documentFiles,
                failedFiles => {
                    if (isArrayNullOrUndefinedOrEmpty(failedFiles)) {
                        this.attachFiles(files);
                    }
                    else {
                        this.mutateState(
                            s => s.unattachableFiles = failedFiles,
                            () => {
                                let attachableFiles = files.filter(x => !failedFiles.some(file => file === x.description));
                                this.attachFiles(attachableFiles);
                            }
                        );
                    }
                }
            );
            return;
        }

        this.attachFiles(files);
    }

    protected renderUnattachableFilesWarning(): void {
        if (isArrayNullOrUndefinedOrEmpty(this.state.unattachableFiles))
            return;

        sessionStore.showPopup({
            content: [`${cacheStore.GetString("Attach_UnsupportedFileTypes")}:`].concat(this.state.unattachableFiles),
            autoWidth: true,
            contentStyle: "list-after-first",
            okAction: () => this.mutateState(s => s.unattachableFiles = [])
        });
    }

    private attachFiles(files: ExportableFile[]): void {
        this.mutateState(s => {
            const newFiles = files
                .filter(x => s.selectedAttachments.all(a => !x.isEquivalent(a.Model)))
                .map(x => x.toExportableFileModel());

            s.selectedAttachments = s.selectedAttachments.concat(newFiles);
            s.showFileSelection = false;
            s.generatedAttachment = null;
        });
    }

    private canDocumentFilesConvertToPDF(documentFiles: ExportableFile[], then?: (failedFiles: string[]) => void): void {
        let preloadedExportableFiles = documentFiles.map(x => x.getFetchUrlDtoAsync());
        Promise.all(preloadedExportableFiles).then(preloadedExportableFile => {
            let request = {
                fetchUrls: preloadedExportableFile.toHash(x => x.FetchUrl, x => x.OriginalExportableFile.description),
                addErrorPages: true
            };

            Client.Postv2(
                ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile,
                request,
                response => then && then(response.FailedFiles),
                error => this.handleError(error)
            );
        });
    }

    protected removeAttachment(itemId: number): void {
        this.mutateState(s => {
            s.selectedAttachments = s.selectedAttachments.filter(x => x.itemId !== itemId);
            s.generatedAttachment = null;
        });
    }

    protected get areSubjectAndBodyValid(): boolean {
        return !!this.state.messageModel.Subject || !!this.state.messageModel.Body;
    }

    protected get isFormValid(): boolean {
        return !isArrayNullOrUndefinedOrEmpty(this.state.recipients) && this.areSubjectAndBodyValid;
    }

    protected toggleSubjectClickableTextVisibility(e: React.MouseEvent): void {
        e.stopPropagation();
        this.mutateState(s => {
            s.isSubjectClickableTextShown = !s.isSubjectClickableTextShown;
            s.isBodyClickableTextShown = false;
        });
    }

    protected toggleBodyClickableTextVisibility(e: React.MouseEvent): void {
        e.stopPropagation();
        this.mutateState(s => {
            s.isBodyClickableTextShown = !s.isBodyClickableTextShown;
            s.isSubjectClickableTextShown = false;
        });
    }

    protected handleSend(): void {
        const requestData = {
            Message: this.state.messageModel,
            RecipientExternalEmployees: this.state.recipients,
            PatientId: this.state.selectedPatient && this.state.selectedPatient.PatientId,
            IsInternalPatient: !!(this.state.selectedPatient && this.state.selectedPatient.isInternal()),
            Attachments: this.state.selectedAttachments.map(x => x.Model.toAttachedObject()),
            GeneratedAttachmentId: this.state.generatedAttachment?.Id
        };

        Client.Postv2(
            ServerOperations.Server.Myle_ExternalCommunication_SaveSendMessage,
            requestData,
            () => {
                this.mutateState(
                    s => {
                        s.shouldClearCacheState = true;
                        s.isSending = false;
                    },
                    () => this.props.onSend && this.props.onSend()
                );
            },
            errorMsgs => this.handleError(errorMsgs)
        );
    }

    protected handleError(errorMsgs: ErrorMessage[]): void {
        if (isArrayNullOrUndefinedOrEmpty(errorMsgs))
            return;

        sessionStore.showPopup(errorMsgs);
        this.mutateState(s => s.isSending = false);
    }

    private containerClick(): void {
        this.mutateState(s => {
            s.isBodyClickableTextShown = false;
            s.isSubjectClickableTextShown = false;
        });
    }
}

export { BaseExternalMessageFormComponent, BaseExternalMessageFormState, IBaseExternalMessageFormProps };
